import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Upload, Trash2, Star, ArrowLeft, GripVertical, X, Plus } from "lucide-react";
import api, { mediaUrl, apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
    name: "", short_description: "", description: "", category: "", subcategory: "",
    mrp: 0, price: 0, stock: 0, warranty: "", shipping_info: "", video_url: "",
    features: [], specifications: [], temperature_settings: [], whats_in_box: [],
    active: true, hidden: false, featured: false, best_seller: false, new_arrival: false,
    seo_title: "", seo_description: "",
};

export default function AdminProductEdit() {
    const { pid } = useParams();
    const navigate = useNavigate();
    const isNew = !pid;
    const [form, setForm] = useState(EMPTY);
    const [images, setImages] = useState([]);
    const [cats, setCats] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef();
    const videoRef = useRef();

    useEffect(() => {
        api.get("/admin/categories").then(({ data }) => setCats(data.categories));
        if (!isNew) {
            api.get(`/admin/products/${pid}`).then(({ data }) => {
                setForm({ ...EMPTY, ...data.product });
                setImages(data.product.images || []);
            });
        }
    }, [pid]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const listField = (k, v) => set(k, v.split("\n").filter((x) => x.trim()));

    const save = async () => {
        if (!form.name.trim()) { toast.error("Product name is required"); return; }
        setSaving(true);
        const payload = {
            ...form,
            mrp: Number(form.mrp), price: Number(form.price), stock: Number(form.stock),
            specifications: form.specifications,
        };
        try {
            if (isNew) {
                const { data } = await api.post("/admin/products", payload);
                toast.success("Product created. You can now upload images.");
                navigate(`/admin/products/${data.product.id}`);
            } else {
                await api.put(`/admin/products/${pid}`, payload);
                toast.success("Product saved");
            }
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    const uploadImages = async (files) => {
        setUploading(true);
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await api.post(`/admin/products/${pid}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                setImages((prev) => [...prev, data.image]);
            } catch (err) { toast.error(apiError(err)); }
        }
        setUploading(false);
        toast.success("Images uploaded");
    };

    const deleteImage = async (media_id) => {
        await api.delete(`/admin/products/${pid}/images/${media_id}`);
        setImages((prev) => prev.filter((i) => i.media_id !== media_id));
    };

    const makeMain = async (media_id) => {
        const ordered = [images.find((i) => i.media_id === media_id), ...images.filter((i) => i.media_id !== media_id)];
        setImages(ordered);
        await api.put(`/admin/products/${pid}/images/reorder`, { images: ordered.map((i) => i.media_id) });
        toast.success("Main image updated");
    };

    const uploadVideo = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            set("video_url", data.url);
            toast.success("Video uploaded");
        } catch (err) { toast.error(apiError(err)); }
        finally { setUploading(false); }
    };

    const selCat = cats.find((c) => c.name === form.category);

    return (
        <div className="max-w-4xl">
            <button onClick={() => navigate("/admin/products")} className="flex items-center gap-2 text-sm text-gray-500 mb-4 hover:text-ink"><ArrowLeft className="h-4 w-4" /> Back to Products</button>
            <h1 className="text-2xl font-semibold mb-6">{isNew ? "Add Product" : "Edit Product"}</h1>

            <div className="space-y-6">
                <Card title="Basic Info">
                    <F label="Product Name"><input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></F>
                    <F label="Short Description"><input data-testid="pf-short" value={form.short_description} onChange={(e) => set("short_description", e.target.value)} className={inp} /></F>
                    <F label="Full Description"><textarea data-testid="pf-desc" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className={inp} /></F>
                    <div className="grid grid-cols-2 gap-4">
                        <F label="Category">
                            <select data-testid="pf-category" value={form.category} onChange={(e) => { set("category", e.target.value); set("subcategory", ""); }} className={inp}>
                                <option value="">Select…</option>
                                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                            </select>
                        </F>
                        <F label="Subcategory">
                            <select data-testid="pf-subcategory" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className={inp}>
                                <option value="">None</option>
                                {(selCat?.subcategories || []).map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </F>
                    </div>
                </Card>

                <Card title="Pricing & Stock">
                    <div className="grid grid-cols-3 gap-4">
                        <F label="MRP (₹)"><input data-testid="pf-mrp" type="number" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} className={inp} /></F>
                        <F label="Selling Price (₹)"><input data-testid="pf-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className={inp} /></F>
                        <F label="Stock"><input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={inp} /></F>
                    </div>
                </Card>

                {!isNew && (
                    <Card title="Product Images">
                        <p className="text-xs text-gray-500 mb-3">Upload real ELVOSH product photos. The first image is the main image. Click the star to set a different main image.</p>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                            {images.map((im, i) => (
                                <div key={im.media_id} className="relative group border border-gray-200 rounded overflow-hidden aspect-square">
                                    <img src={mediaUrl(im.url)} alt="" className="w-full h-full object-cover" />
                                    {i === 0 && <span className="absolute top-1 left-1 bg-gold text-white text-[9px] px-1.5 py-0.5 rounded">MAIN</span>}
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                        {i !== 0 && <button onClick={() => makeMain(im.media_id)} title="Set as main" className="p-1.5 bg-white rounded"><Star className="h-3.5 w-3.5" /></button>}
                                        <button onClick={() => deleteImage(im.media_id)} className="p-1.5 bg-white rounded text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                </div>
                            ))}
                            <button data-testid="pf-upload-image" onClick={() => fileRef.current.click()} disabled={uploading} className="aspect-square border-2 border-dashed border-gray-300 rounded flex flex-col items-center justify-center text-gray-400 hover:border-gold hover:text-gold">
                                <Upload className="h-5 w-5" /><span className="text-[10px] mt-1">{uploading ? "…" : "Upload"}</span>
                            </button>
                        </div>
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadImages(Array.from(e.target.files))} />
                        <div className="mt-4">
                            <F label="Product Video">
                                <div className="flex items-center gap-3">
                                    <button onClick={() => videoRef.current.click()} className="border border-gray-300 rounded px-3 py-2 text-sm flex items-center gap-2"><Upload className="h-4 w-4" /> Upload Video</button>
                                    {form.video_url && <span className="text-xs text-green-600">Video attached</span>}
                                    {form.video_url && <button onClick={() => set("video_url", "")} className="text-red-500"><X className="h-4 w-4" /></button>}
                                </div>
                                <input ref={videoRef} type="file" accept="video/*" hidden onChange={(e) => e.target.files[0] && uploadVideo(e.target.files[0])} />
                            </F>
                        </div>
                    </Card>
                )}

                <Card title="Details (one item per line)">
                    <F label="Features"><textarea data-testid="pf-features" rows={4} value={form.features.join("\n")} onChange={(e) => listField("features", e.target.value)} className={inp} /></F>
                    <F label="What's in the Box"><textarea rows={3} value={form.whats_in_box.join("\n")} onChange={(e) => listField("whats_in_box", e.target.value)} className={inp} /></F>
                    <F label="Temperature Settings"><textarea rows={2} value={form.temperature_settings.join("\n")} onChange={(e) => listField("temperature_settings", e.target.value)} className={inp} /></F>
                    <F label="Warranty"><input value={form.warranty} onChange={(e) => set("warranty", e.target.value)} className={inp} /></F>
                    <F label="Shipping Info"><input value={form.shipping_info} onChange={(e) => set("shipping_info", e.target.value)} className={inp} /></F>
                    <SpecsEditor specs={form.specifications} onChange={(v) => set("specifications", v)} />
                </Card>

                <Card title="Visibility & SEO">
                    <div className="flex flex-wrap gap-4 mb-4">
                        {["active", "hidden", "featured", "best_seller", "new_arrival"].map((k) => (
                            <label key={k} className="flex items-center gap-2 text-sm capitalize"><input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="accent-gold" data-testid={`pf-${k}`} /> {k.replace("_", " ")}</label>
                        ))}
                    </div>
                    <F label="SEO Title"><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inp} /></F>
                    <F label="SEO Description"><textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={inp} /></F>
                </Card>

                <button data-testid="pf-save" onClick={save} disabled={saving} className="bg-ink text-white px-8 py-3 rounded text-sm hover:bg-gray-800 disabled:opacity-50">{saving ? "Saving…" : isNew ? "Create Product" : "Save Changes"}</button>
            </div>
        </div>
    );
}

const inp = "w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-gold";
function Card({ title, children }) { return <div className="bg-white border border-gray-200 rounded-lg p-6"><h3 className="font-medium mb-4">{title}</h3><div className="space-y-4">{children}</div></div>; }
function F({ label, children }) { return <div><label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>{children}</div>; }

function SpecsEditor({ specs, onChange }) {
    const add = () => onChange([...specs, { label: "", value: "" }]);
    const update = (i, k, v) => onChange(specs.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
    const remove = (i) => onChange(specs.filter((_, idx) => idx !== i));
    return (
        <F label="Specifications">
            <div className="space-y-2">
                {specs.map((s, i) => (
                    <div key={i} className="flex gap-2">
                        <input placeholder="Label" value={s.label} onChange={(e) => update(i, "label", e.target.value)} className={inp} />
                        <input placeholder="Value" value={s.value} onChange={(e) => update(i, "value", e.target.value)} className={inp} />
                        <button onClick={() => remove(i)} className="text-red-500 px-2"><X className="h-4 w-4" /></button>
                    </div>
                ))}
                <button onClick={add} className="text-sm text-gold flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add specification</button>
            </div>
        </F>
    );
}
